import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Slider from 'react-slick';
import { sectionVariants } from './animations';
import ExactDateSelection from './ExactDateSelection';
import dayjs from 'dayjs';
import '../../search/assets/css/date-search.css';

export const isValidDateSelected = (dateSearchState) => {
  return (
    (dateSearchState?.dateMethod === 'Flexible Search' &&
      dateSearchState?.selectedMonths?.length) ||
    (dateSearchState?.bookingDates &&
      dateSearchState.bookingDates[0] &&
      dateSearchState.bookingDates[0].startDate &&
      dateSearchState.bookingDates[0]?.startDate &&
      dayjs(dateSearchState.bookingDates[0]?.startDate).isValid())
  );
};

export const getMonthRangeForFlexibleSearch = (dateSearchState) => {
  if (
    dateSearchState?.dateMethod === 'Flexible Search' &&
    dateSearchState?.selectedMonths &&
    dateSearchState?.selectedMonths?.length
  ) {
    const parsedMonths = dateSearchState.selectedMonths.map((m) => m.date);
    const firstMonth = parsedMonths.at(0);
    const lastMonth = parsedMonths.at(-1);
    return [firstMonth, lastMonth];
  }
  return [];
};

export const SelectedDateText = ({
  dateSearchState,
  dateFormat = 1,
  placeholder = 'Add Dates',
}) => {
  if (
    dateSearchState?.dateMethod === 'Flexible Search' &&
    dateSearchState?.selectedMonths?.length
  ) {
    const [firstMonth, lastMonth] =
      getMonthRangeForFlexibleSearch(dateSearchState);
    if (firstMonth && lastMonth) {
      if (!firstMonth.isSame(lastMonth, 'month')) {
        return `${firstMonth.format('MMM')} - ${lastMonth.format('MMM YYYY')}`;
      }
      return firstMonth.format('MMMM YYYY');
    }
  }
  if (
    dateSearchState?.bookingDates &&
    dateSearchState.bookingDates.length > 0 &&
    dateSearchState.bookingDates[0]?.startDate
  ) {
    const startDate = dayjs(dateSearchState.bookingDates[0].startDate);
    const endDate = dayjs(dateSearchState.bookingDates[0].endDate);
    if (dateFormat === 2) {
      if (startDate.isSame(endDate, 'day')) {
        // Same day → "Aug 10"
        return startDate.format('MMM D');
      } else if (startDate.month() === endDate.month()) {
        // Same month → "Aug 10 - 15"
        return `${startDate.format('MMM D')} - ${endDate.format('D')}`;
      }
      // Different months → "Aug 28 - Sep 3"
      return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;
    }
    if (startDate.isSame(dateSearchState.bookingDates[0].endDate, 'day')) {
      return startDate.format('DD MMM YYYY');
    }
    return `${startDate.format('DD MMM')} - ${endDate.format('DD MMM YYYY')}`;
  }
  return placeholder;
};

const DateSelectionSection = ({
  collapseOpen,
  dateSearchState,
  updateDateSearchState,
  handleDateChange,
  months,
  settings,
  monthCalendar,
  monthCalendarTick,
  withAnimation = true,
}) => {
  const monthIdInRange = (monthId) => {
    const range = dateSearchState?.selectedMonths;
    if (!range || !range.length) {
      return false;
    }
    if (range.length === 1) {
      return range[0].id === monthId;
    }
    const month = dayjs(monthId, 'MMMM-YYYY');
    return (
      month.isSameOrBefore(range.at(-1).date, 'month') &&
      month.isSameOrAfter(range.at(0).date, 'month')
    );
  };

  const content = (
    <div className='section-content'>
      <div className='search-toggle-container mg-bottom-10'>
        <span className='search-label fs-14 gMedium'>
          {dateSearchState?.dateMethod === 'Flexible Search'
            ? 'Flexible Search'
            : 'Select Date Range'}
        </span>
        <button
          onClick={() =>
            updateDateSearchState(
              'dateMethod',
              dateSearchState?.dateMethod === 'Exact Dates'
                ? 'Flexible Search'
                : 'Exact Dates'
            )
          }
          className={`search-toggle-button ${
            dateSearchState?.dateMethod === 'Exact Dates' ? 'active' : ''
          }`}
        >
          {dateSearchState?.dateMethod === 'Flexible Search'
            ? 'Exact Date'
            : "I'm Flexible"}
        </button>
      </div>
      {dateSearchState?.dateMethod === 'Exact Dates' && (
        <ExactDateSelection
          dateSearchState={dateSearchState}
          updateDateSearchState={updateDateSearchState}
          handleDateChange={handleDateChange}
        />
      )}
      {dateSearchState?.dateMethod === 'Flexible Search' && (
        <div className='month-carousel-wrapper'>
          <Slider {...settings}>
            {months.map((month) => (
              <div key={month.id} className='carousel-item-wrapper'>
                <div
                  className={`month-card ${
                    monthIdInRange(month.id) ? 'active' : ''
                  }`}
                  onClick={() =>
                    updateDateSearchState('selectedMonths', (prev) => {
                      const selected = {
                        date: dayjs(month.id, 'MMMM-YYYY'),
                        id: month.id,
                      };
                      if (prev?.length === 1) {
                        return [selected, ...prev].toSorted((a, b) =>
                          a.date.diff(b.date, 'month')
                        );
                      }
                      return [selected];
                    })
                  }
                >
                  <div className='calendar-icon'>
                    {monthIdInRange(month.id) ? (
                      <img src={monthCalendarTick} alt='Selected month' />
                    ) : (
                      <img src={monthCalendar} alt='Calendar' />
                    )}
                  </div>
                  <div className='month-text'>{month.label}</div>
                  <div className='year-text'>{month.year}</div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );

  if (!withAnimation) {
    return collapseOpen === 'when' ? content : null;
  }

  return (
    <AnimatePresence>
      {collapseOpen === 'when' && (
        <motion.div
          initial='closed'
          animate='open'
          exit='closed'
          variants={sectionVariants}
          style={{ overflow: 'hidden' }}
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DateSelectionSection;
